import { useState, useRef, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import img from "../../assets/images/xpope.png";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./Login.css";
import {
  login,
  resetForgotPassword,
  sendEmailOTP,
  verifyOtp,
  verifyUserMail,
} from "../../config/LiveApi";
import { useDispatch } from "react-redux";
import userSlice from "../../redux/slices/userSlice";
import { MdRemoveRedEye, MdVisibilityOff } from "react-icons/md";

const RECAPTCHA_SITEKEY = import.meta.env.VITE_RECAPTCHA_SITEKEY;
const ENVIRONMENT = import.meta.env.VITE_APP_ENV;
const { actions } = userSlice;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifyMail, setVerifyMail] = useState(false);
  const [changePass, setChangePass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    newPassword: "",
    confPassword: "",
    otp: "",
  });
  const [otpTimer, setOtpTimer] = useState(0);

  const hasUppercase = /[A-Z]/.test(data.newPassword);
  const hasNumber = /\d/.test(data.newPassword);
  const hasSpecialChar = /[@$!%*?&]/.test(data.newPassword);
  const isMinLength = data.newPassword.length >= 8;

  const isPasswordValid =
    hasUppercase && hasNumber && hasSpecialChar && isMinLength;
  const passwordsMatch =
    data.newPassword === data.confPassword || data.confPassword === "";

  const reCaptchaRef = useRef();
  const userIdRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    let timer;
    if (otpTimer > 0) {
      timer = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpTimer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token =
      ENVIRONMENT === "prod" ? await reCaptchaRef.current.executeAsync() : "";

    setLoading(true);
    try {
      const result = await login(email, password, token);
      if (result?.accessToken) {
        setLoading(false);
      }
      const expiryTime = Date.now() + Number(result?.refreshTokenExpiry);

      dispatch(actions.setItem(["token", result.accessToken]));
      dispatch(actions.setItem(["refreshToken", result.refreshToken]));
      dispatch(actions.setItem(["refreshTokenExpiry", expiryTime]));
      dispatch(actions.setItem(["role", result.user.role]));
      dispatch(actions.setItem(["userName", result.user.name]));
      dispatch(actions.setItem(["userEmail", result.user.email]));
      dispatch(actions.setItem(["userPhoneNumber", result.user.phoneNumber]));
      dispatch(actions.setItem(["userId", result.user.id]));
      dispatch(
        actions.setItem(["payoutFormAccess", result.user.payoutFormAccess])
      );

      const role = result.user.role;
      
      if (role === "operation" || role === "super") {
        sessionStorage.setItem("openAlertsOnLogin", "1");
      }
      if (role === "mops") navigate("/pay-in");
      else if (role === "pros") navigate("/transaction-lookup");
      else if (role === "accounts") navigate("/view-settlement");
      else if (role === "operation") navigate("/usermanagement");
      else if (role === "fundLoad") navigate("/merchant-settlement");
      else if (role === "compliance") navigate("/chargeback-dashboard");
      else if (role === "trafficManager") navigate("/user-provider-set");
      else if (role === "reseller") navigate("/get-reseller-data");
      else navigate("/dashboard");

      toast.success("Login successful!", { autoClose: 5000 });
      ENVIRONMENT === "prod" && reCaptchaRef.current.reset();
    } catch (err) {
      toast.error("Login failed. Please check your credentials.", {
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setData({ ...data, [name]: value });
  };

  const handleMailVerify = async (e) => {
    e.preventDefault();
    setOtpTimer(30);

    try {
      const response = await verifyUserMail(email);
      userIdRef.current = response?.data?.[0]?.id;

      if (response.status !== 200) {
        setEmail("");
        toast.error(response.message);
        return;
      }

      toast.success(response.message);
      const otpResponse = await sendEmailOTP(userIdRef.current);

      if (otpResponse.status !== 201) {
        setEmail("");
        toast.error(otpResponse.message);
      }
    } catch (err) {
      toast.error("Something went wrong while verifying email.");
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();

    const response = await verifyOtp(userIdRef.current, data.otp);

    if (response.status !== 200) {
      setData({
        newPassword: "",
        confPassword: "",
        entOtp: "",
      });
      toast.error(response.message);
      return;
    }

    toast.success(response.message);
    setChangePass(true);

    return;
  };

  const handleChangePass = async (e) => {
    e.preventDefault();

    try {
      const response = await resetForgotPassword(userIdRef.current, data);

      if (response.status !== 200) toast.error(response.message);
      else {
        setData({
          newPassword: "",
          confPassword: "",
          otp: "",
        });

        setChangePass(false);
        setVerifyMail(false);
        toast.success(response.message);
      }
    } catch (err) {
      toast.error("Error changing password:", err.message);
    }
  };

  return (
    <div className="container">
      <div className="left-column-1">
        <img
          className="head-text-xpo"
          src={img}
          alt="Image"
          width="400"
          height="100"
        />
      </div>
      <div className="right-column">
        {!verifyMail && !changePass && (
          <div className="login-form">
            <h2>Log In</h2>
            <form onSubmit={handleSubmit} method="post">
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                />
              </div>
              <div className="form-group password-wrapper">
                <label htmlFor="password">Password:</label>
                <div className="password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className="for-pass"
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    required
                  />
                  {showPassword ? (
                    <MdVisibilityOff
                      className="eye-icon"
                      onClick={() => setShowPassword(false)}
                    />
                  ) : (
                    <MdRemoveRedEye
                      className="eye-icon"
                      onClick={() => setShowPassword(true)}
                    />
                  )}
                </div>
              </div>
              {ENVIRONMENT === "prod" && (
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITEKEY}
                  size="invisible"
                  ref={reCaptchaRef}
                />
              )}

              <button type="submit" className="blue-button">
                {loading ? "Logging in..." : "Log In"}
              </button>

              <div className="forgot-password-container">
                <a
                  className="forgot-password-link"
                  onClick={() => {
                    setVerifyMail(true);
                    setEmail("");
                  }}
                >
                  Forgot Password?
                </a>
              </div>
            </form>
          </div>
        )}

        {verifyMail && !changePass && (
          <div className="login-form">
            <h2>Verify Email</h2>
            <form method="post">
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  className="email-otp-inp"
                  name="email"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  value={email}
                  required
                />
              </div>

              <div className="form-group otp-group">
                <label htmlFor="otp">OTP:</label>
                <div className="otp-field-wrapper">
                  <input
                    type="text"
                    className="email-otp-inp"
                    name="otp"
                    placeholder="Enter OTP"
                    value={data.otp}
                    onChange={handleInputChange}
                    autoComplete="off"
                    required
                  />
                  <button
                    type="button"
                    className="get-otp-btn"
                    disabled={!email || otpTimer > 0}
                    onClick={handleMailVerify}
                  >
                    {otpTimer > 0 ? `Wait ${otpTimer}s` : "Get OTP"}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="submit-mail-btn"
                onClick={handleOtpVerify}
              >
                Verify OTP
              </button>

              <div className="forgot-password-container">
                <a
                  href="/"
                  className="forgot-password-link"
                  onClick={() => setVerifyMail(false)}
                >
                  Back to Login
                </a>
              </div>
            </form>
          </div>
        )}

        {changePass && (
          <div className="login-form">
            <h2>Change Password</h2>
            <form method="post">
              <div className="form-group">
                <label htmlFor="newPassword">New Password:</label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="newPassword"
                  className="for-pass"
                  onChange={handleInputChange}
                  placeholder="New Password"
                  required
                />

                {data.newPassword && !isPasswordValid && (
                  <ul className="password-error-list">
                    {!hasSpecialChar && (
                      <li>
                        Password must contain at least one special character.
                      </li>
                    )}
                    {!(hasNumber && hasUppercase) && (
                      <li>
                        Password must contain at least one number and one
                        capital letter.
                      </li>
                    )}
                    {!isMinLength && (
                      <li>
                        Password length must be at least 8 characters long.
                      </li>
                    )}
                  </ul>
                )}
              </div>
              <div className="form-group password-wrapper">
                <label htmlFor="confPassword">Confirm Password:</label>
                <div className="password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="confPassword"
                    className="for-pass"
                    onChange={handleInputChange}
                    placeholder="Confirm Password"
                    required
                  />
                  {showPassword ? (
                    <MdRemoveRedEye
                      className="eye-icon"
                      onClick={() => setShowPassword(false)}
                    />
                  ) : (
                    <MdVisibilityOff
                      className="eye-icon"
                      onClick={() => setShowPassword(true)}
                    />
                  )}
                </div>

                {!passwordsMatch && data.confPassword && (
                  <p className="password-error-message">
                    Passwords don&apos;t match{" "}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="submit-mail-btn"
                onClick={handleChangePass}
              >
                Change Password
              </button>

              <div className="forgot-password-container">
                <a
                  href="/"
                  className="forgot-password-link"
                  onClick={() => {
                    setChangePass(false);
                    setVerifyMail(false);
                  }}
                >
                  Back to Login
                </a>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
